// ---------------------------------------------------------------------------
// EvaluationTemplateForm — pure validation.
//
// Single function that takes the form's structural + environmental state
// and returns the flags the Save button needs. Lifted out so the form's
// body doesn't carry 60+ lines of predicates inline. React-free.
// ---------------------------------------------------------------------------

import { isValidAgentName } from '../../../types/agent.js';
import { isValidEvaluationTemplateName } from '../../../types/evaluationTemplate.js';

import {
  VARIABLE_NAME_PATTERN,
  type AgentDraft,
  type DataLakeFileSelector,
  type DataSourceMode,
  type TaskDraft,
  type VariableRow,
} from './EvaluationTemplateForm.types.js';
import { isValidTaskName } from './EvaluationTemplateForm.utils.js';

export interface FormValidityParams {
  // Form state
  jobName: string;
  taskDrafts: TaskDraft[];
  variables: VariableRow[];
  querySetMode: DataSourceMode;
  querySetItems: string[] | null;
  dataSetEnabled: boolean;
  dataSetMode: DataSourceMode;
  dataSetExistingId: string | null;
  dataSetFileSelector: DataLakeFileSelector[];
  dataSetItems: string[] | null;
  bindingFlowHasErrors?: boolean;
  // Environmental gates
  submitting: boolean;
  workspaceLoading: boolean;
  activeWorkspaceName: string | null | undefined;
}

export interface FormValidity {
  /** Flat list of every agent across every task — used by submit too. */
  allAgents: AgentDraft[];
  /** True when the Save button should be enabled. */
  canSubmit: boolean;
}

export function getFormValidity(p: FormValidityParams): FormValidity {
  const variablesValid = p.variables.every(
    (v) => v.name.length === 0 || VARIABLE_NAME_PATTERN.test(v.name),
  );
  const variableNamesUnique =
    new Set(p.variables.map((v) => v.name).filter((n) => n.length > 0)).size ===
    p.variables.filter((v) => v.name.length > 0).length;

  // Agents are references to existing workspace agents — only the chosen
  // name needs to be valid.
  const allAgents = p.taskDrafts.map((t) => t.agent);
  const allAgentsValid = allAgents.every((a) => isValidAgentName(a.name));

  const allGraderRows = p.taskDrafts.flatMap((t) => t.graderRows);
  const gradersValid = allGraderRows.every((r) => r.name.length > 0);

  const taskNamesValid = p.taskDrafts.every((t) => isValidTaskName(t.name));

  const shapeValid = p.taskDrafts.length >= 1;

  // Data set is valid when either disabled, existing-and-selected (with at
  // least one file selector, which the backend requires), or
  // inline/file-and-non-empty.
  const dataSetValid =
    !p.dataSetEnabled ||
    (p.dataSetMode === 'existing' &&
      p.dataSetExistingId !== null &&
      p.dataSetFileSelector.length > 0) ||
    (p.dataSetMode !== 'existing' &&
      p.dataSetItems !== null &&
      p.dataSetItems.length > 0);

  const canSubmit =
    !p.submitting &&
    !p.workspaceLoading &&
    p.activeWorkspaceName !== null &&
    p.activeWorkspaceName !== undefined &&
    isValidEvaluationTemplateName(p.jobName) &&
    p.querySetMode !== 'existing' &&
    p.querySetItems !== null &&
    p.querySetItems.length > 0 &&
    dataSetValid &&
    allAgentsValid &&
    taskNamesValid &&
    shapeValid &&
    variablesValid &&
    variableNamesUnique &&
    gradersValid &&
    !p.bindingFlowHasErrors;

  return { allAgents, canSubmit };
}
